/**
 * Utility for fetching and caching LLM model pricing from BerriAI/litellm.
 * Falls back to a static table if fetch fails.
 */

const PRICING_URL = "https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json";
const CACHE_KEY = "llm_model_pricing_json";
const CACHE_FAIL_KEY = "llm_model_pricing_last_fail_ts";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const FAIL_BACKOFF_MS = 10 * 60 * 1000; // 10 minutes
let inFlightPricingPromise: Promise<Record<string, any>> | null = null;

export const FALLBACK_PRICING: Record<string, any> = {
  // OpenAI Models
  "gpt-5": {
    input_cost_per_1k_tokens: 0.00125,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 400000,
  },
  "gpt-5-mini": {
    input_cost_per_1k_tokens: 0.00025,
    output_cost_per_1k_tokens: 0.002,
    max_input_tokens: 400000,
  },
  "gpt-5-nano": {
    input_cost_per_1k_tokens: 0.00005,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 400000,
  },
  "gpt-5.1": {
    input_cost_per_1k_tokens: 0.00125,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 400000,
  },
  "gpt-5.2": {
    input_cost_per_1k_tokens: 0.00175,
    output_cost_per_1k_tokens: 0.014,
    max_input_tokens: 272000,
  },
  "gpt-5.2-codex": {
    input_cost_per_1k_tokens: 0.00175,
    output_cost_per_1k_tokens: 0.014,
    max_input_tokens: 272000,
  },
  "gpt-5.2-pro": {
    input_cost_per_1k_tokens: 0.021,
    output_cost_per_1k_tokens: 0.168,
    max_input_tokens: 272000,
  },
  "gpt-4-turbo": {
    input_cost_per_1k_tokens: 0.01,
    output_cost_per_1k_tokens: 0.03,
    max_input_tokens: 128000,
  },
  "gpt-4o": {
    input_cost_per_1k_tokens: 0.0025,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 128000,
  },
  "gpt-3.5-turbo": {
    input_cost_per_1k_tokens: 0.0005,
    output_cost_per_1k_tokens: 0.0015,
    max_input_tokens: 16385,
  },
  "gpt-4.1": {
    input_cost_per_1k_tokens: 0.002,
    output_cost_per_1k_tokens: 0.008,
    max_input_tokens: 1047576,
  },
  "gpt-4.1-mini": {
    input_cost_per_1k_tokens: 0.0004,
    output_cost_per_1k_tokens: 0.0016,
    max_input_tokens: 1047576,
  },
  "gpt-4.1-nano": {
    input_cost_per_1k_tokens: 0.0001,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 1047576,
  },
  "o4-mini": {
    input_cost_per_1k_tokens: 0.0011,
    output_cost_per_1k_tokens: 0.0044,
    max_input_tokens: 200000,
  },

  // OpenRouter Models
  "openai/gpt-4-turbo": {
    input_cost_per_1k_tokens: 0.01,
    output_cost_per_1k_tokens: 0.03,
    max_input_tokens: 128000,
  },
  "openai/gpt-4o": {
    input_cost_per_1k_tokens: 0.0025,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 128000,
  },
  "openai/gpt-3.5-turbo": {
    input_cost_per_1k_tokens: 0.0005,
    output_cost_per_1k_tokens: 0.0015,
    max_input_tokens: 16385,
  },
  "openai/gpt-4.1": {
    input_cost_per_1k_tokens: 0.002,
    output_cost_per_1k_tokens: 0.008,
    max_input_tokens: 1047576,
  },
  "openai/gpt-4.1-mini": {
    input_cost_per_1k_tokens: 0.0004,
    output_cost_per_1k_tokens: 0.0016,
    max_input_tokens: 1047576,
  },
  "openai/gpt-4.1-nano": {
    input_cost_per_1k_tokens: 0.0001,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 1047576,
  },
  "openai/gpt-5": {
    input_cost_per_1k_tokens: 0.00125,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 400000,
  },
  "openai/gpt-5-mini": {
    input_cost_per_1k_tokens: 0.00025,
    output_cost_per_1k_tokens: 0.002,
    max_input_tokens: 400000,
  },
  "openai/gpt-5-nano": {
    input_cost_per_1k_tokens: 0.00005,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 400000,
  },
  "openai/gpt-5.1": {
    input_cost_per_1k_tokens: 0.00125,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 400000,
  },
  "openai/gpt-5.1-mini": {
    input_cost_per_1k_tokens: 0.00025,
    output_cost_per_1k_tokens: 0.002,
    max_input_tokens: 400000,
  },
  "openai/gpt-5.1-nano": {
    input_cost_per_1k_tokens: 0.00005,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 400000,
  },
  "openai/gpt-5.2": {
    input_cost_per_1k_tokens: 0.00175,
    output_cost_per_1k_tokens: 0.014,
    max_input_tokens: 272000,
  },
  "openai/gpt-5.2-pro": {
    input_cost_per_1k_tokens: 0.021,
    output_cost_per_1k_tokens: 0.168,
    max_input_tokens: 272000,
  },
  "openai/gpt-5.2-codex": {
    input_cost_per_1k_tokens: 0.00175,
    output_cost_per_1k_tokens: 0.014,
    max_input_tokens: 272000,
  },
  "openai/gpt-5.3-codex": {
    input_cost_per_1k_tokens: 0.00175,
    output_cost_per_1k_tokens: 0.014,
    max_input_tokens: 272000,
  },
  "google/gemini-2.0-flash-001": {
    input_cost_per_1k_tokens: 0.00015,
    output_cost_per_1k_tokens: 0.0006,
    max_input_tokens: 1048576,
  },
  "google/gemini-3-flash-preview": {
    input_cost_per_1k_tokens: 0.0005,
    output_cost_per_1k_tokens: 0.003,
    max_input_tokens: 1048576,
  },
  "google/gemini-3.1-pro-preview": {
    input_cost_per_1k_tokens: 0.002,
    output_cost_per_1k_tokens: 0.012,
    max_input_tokens: 1048576,
  },

  // Google Models
  "gemini-1.5-pro": {
    input_cost_per_1k_tokens: 0.00125,
    output_cost_per_1k_tokens: 0.005,
    max_input_tokens: 2097152,
  },
  "gemini-1.5-flash": {
    input_cost_per_1k_tokens: 0.000075,
    output_cost_per_1k_tokens: 0.0003,
    max_input_tokens: 1000000,
  },
  "gemini-1.5-flash-8b": {
    input_cost_per_1k_tokens: 0.0000375,
    output_cost_per_1k_tokens: 0.00015,
    max_input_tokens: 1000000,
  },
  "gemini-2.0-flash": {
    input_cost_per_1k_tokens: 0.0001,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 1000000,
  },
  "gemini-2.5-pro": {
    input_cost_per_1k_tokens: 0.00125,
    output_cost_per_1k_tokens: 0.01,
    max_input_tokens: 2000000,
  },
  "gemini-2.5-flash": {
    input_cost_per_1k_tokens: 0.0003,
    output_cost_per_1k_tokens: 0.0025,
    max_input_tokens: 1000000,
  },
  "gemini-2.5-flash-lite": {
    input_cost_per_1k_tokens: 0.0001,
    output_cost_per_1k_tokens: 0.0004,
    max_input_tokens: 1000000,
  },
  "gemini-3-pro-preview": {
    input_cost_per_1k_tokens: 0.002,
    output_cost_per_1k_tokens: 0.012,
    max_input_tokens: 2000000,
  },
  "gemini-3-flash-preview": {
    input_cost_per_1k_tokens: 0.0005,
    output_cost_per_1k_tokens: 0.003,
    max_input_tokens: 1048576,
  },
  "gemini-3.1-pro-preview": {
    input_cost_per_1k_tokens: 0.002,
    output_cost_per_1k_tokens: 0.012,
    max_input_tokens: 1048576,
  },

  // Anthropic Models
  "claude-3.7-sonnet": {
    input_cost_per_1k_tokens: 0.003,
    output_cost_per_1k_tokens: 0.015,
    max_input_tokens: 200000,
  },
  "claude-3.5-sonnet": {
    input_cost_per_1k_tokens: 0.003,
    output_cost_per_1k_tokens: 0.015,
    max_input_tokens: 200000,
  },
  "claude-3-opus": {
    input_cost_per_1k_tokens: 0.015,
    output_cost_per_1k_tokens: 0.075,
    max_input_tokens: 200000,
  },
  "claude-3-haiku": {
    input_cost_per_1k_tokens: 0.00025,
    output_cost_per_1k_tokens: 0.00125,
    max_input_tokens: 200000,
  },
  "claude-sonnet-4": {
    input_cost_per_1k_tokens: 0.003,
    output_cost_per_1k_tokens: 0.015,
    max_input_tokens: 200000,
  },
  "anthropic/claude-sonnet-4": {
    input_cost_per_1k_tokens: 0.003,
    output_cost_per_1k_tokens: 0.015,
    max_input_tokens: 200000,
  },
  "claude-opus-4.1": {
    input_cost_per_1k_tokens: 0.015,
    output_cost_per_1k_tokens: 0.075,
    max_input_tokens: 200000,
  },
  "anthropic/claude-opus-4.1": {
    input_cost_per_1k_tokens: 0.015,
    output_cost_per_1k_tokens: 0.075,
    max_input_tokens: 200000,
  },

  // Mistral Models
  "mistral-large": {
    input_cost_per_1k_tokens: 0.008,
    output_cost_per_1k_tokens: 0.024,
    max_input_tokens: 32000,
  },
  "mixtral-8x7b": {
    input_cost_per_1k_tokens: 0.0007,
    output_cost_per_1k_tokens: 0.002,
    max_input_tokens: 32000,
  },

  // Meta Models
  "llama-3-70b-instruct": {
    input_cost_per_1k_tokens: 0.0007,
    output_cost_per_1k_tokens: 0.0012,
    max_input_tokens: 8192,
  },
  "llama-3-8b-instruct": {
    input_cost_per_1k_tokens: 0.0007,
    output_cost_per_1k_tokens: 0.0012,
    max_input_tokens: 8192,
  },

  // Free Models
  "deepseek-v3-base:free": {
    input_cost_per_1k_tokens: 0,
    output_cost_per_1k_tokens: 0,
    max_input_tokens: 8192,
  },
  "qwen2.5-vl-32b-instruct:free": {
    input_cost_per_1k_tokens: 0,
    output_cost_per_1k_tokens: 0,
    max_input_tokens: 32768,
  },

  // xAI Models
  "x-ai/grok-4-fast": {
    input_cost_per_1k_tokens: 0.005,
    output_cost_per_1k_tokens: 0.015,
    max_input_tokens: 128000,
  },
  "x-ai/grok-code-fast-1": {
    input_cost_per_1k_tokens: 0.003,
    output_cost_per_1k_tokens: 0.009,
    max_input_tokens: 128000,
  }
};

export type ModelPricing = {
  input_cost_per_token?: number;
  output_cost_per_token?: number;
  input_cost_per_1k_tokens?: number;
  output_cost_per_1k_tokens?: number;
  max_input_tokens?: number;
};

export function resolveModelPricing(
  pricing: Record<string, any> | null,
  fallback: Record<string, any>,
  provider: string,
  model: string
): ModelPricing | null {
  try {
    if (!model) return null;
    const full = model;
    const base = model.replace(/^[^/]+\//, "");
    const dotted = base.replace(/-/g, ".");
    const dashed = base.replace(/\./g, "-");
    // Handle alternate Gemini naming used by some providers/UI lists:
    // gemini-flash-1.5 <-> gemini-1.5-flash, gemini-pro-1.5 <-> gemini-1.5-pro
    const geminiReordered = base.replace(
      /^gemini-(flash|pro)-(\d+(?:\.\d+)*)$/,
      "gemini-$2-$1"
    );
    const geminiReorderedWithProvider = full.replace(
      /(\/)gemini-(flash|pro)-(\d+(?:\.\d+)*)$/,
      "$1gemini-$3-$2"
    );

    const extraForOpenRouter = provider === "OpenRouter"
      ? [
        `openrouter/${full}`, // e.g. openrouter/anthropic/claude-sonnet-4
        `openrouter/${base}`, // e.g. openrouter/gpt-5.2 (legacy/simple key style)
        `openai/${base}` // keep compatibility with openai/* aliases in fallback/live data
      ]
      : [];
    const candidates = Array.from(new Set([
      full,
      base,
      dotted,
      dashed,
      geminiReordered,
      geminiReorderedWithProvider,
      ...extraForOpenRouter
    ]));

    const sources = [pricing ?? {}, fallback ?? {}];
    for (const src of sources) {
      for (const key of candidates) {
        const v = src[key];
        if (v) return v;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export async function getModelPricing(): Promise<Record<string, any>> {
  if (inFlightPricingPromise) return inFlightPricingPromise;

  inFlightPricingPromise = (async () => {
    // Try cache first
    let staleCachedData: Record<string, any> | null = null;
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        staleCachedData = data ?? null;
        if (Date.now() - timestamp < CACHE_TTL_MS) {
          try { console.debug("[pricing] Using cached model pricing"); } catch { }
          return data;
        }
      } catch {
        // Ignore parse errors, fall through to fetch
      }
    }

    // If we recently failed fetching, avoid hammering the upstream URL.
    const lastFailRaw = localStorage.getItem(CACHE_FAIL_KEY);
    if (lastFailRaw) {
      const lastFail = Number(lastFailRaw);
      if (!Number.isNaN(lastFail) && Date.now() - lastFail < FAIL_BACKOFF_MS) {
        try { console.debug("[pricing] Skipping remote fetch due to failure backoff"); } catch { }
        return staleCachedData ?? FALLBACK_PRICING;
      }
    }

    // Fetch from remote
    try {
      const resp = await fetch(PRICING_URL, { cache: "reload" });
      if (!resp.ok) throw new Error("Failed to fetch pricing JSON");
      const data = await resp.json();
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
      localStorage.removeItem(CACHE_FAIL_KEY);
      try { console.debug("[pricing] Loaded model pricing from remote and cached"); } catch { }
      return data;
    } catch (e) {
      localStorage.setItem(CACHE_FAIL_KEY, String(Date.now()));
      if (staleCachedData) {
        try { console.debug("[pricing] Using stale cached model pricing due to fetch error:", e); } catch { }
        return staleCachedData;
      }
      // Fallback to static
      try { console.debug("[pricing] Using FALLBACK_PRICING due to fetch error:", e); } catch { }
      return FALLBACK_PRICING;
    }
  })();

  try {
    return await inFlightPricingPromise;
  } finally {
    inFlightPricingPromise = null;
  }
}
